import { StandardEvents } from '@shopify/events';

class CustomDefaultProduct extends HTMLElement {
  connectedCallback() {
    this.galleryImage = this.querySelector('[data-cdp-main-image]');
    this.galleryButtons = this.querySelectorAll('[data-cdp-thumb]');
    this.galleryMedia = this.querySelector('.cdp__main-media');
    this.galleryContent = this.querySelector('[data-cdp-main-media-content]');
    this.galleryProgress = this.querySelector('[data-cdp-media-progress]');
    this.currentMediaIndex = Math.max(0, Array.from(this.galleryButtons).findIndex((button) => button.getAttribute('aria-current') === 'true'));
    this.advisorDrawer = this.querySelector('[data-cdp-drawer]');
    this.openAdvisorButtons = this.querySelectorAll('[data-cdp-open-advisor]');
    this.closeAdvisorButtons = this.querySelectorAll('[data-cdp-close-advisor]');
    this.optionDrawers = this.querySelectorAll('[data-cdp-option-drawer]');
    this.openOptionButtons = this.querySelectorAll('[data-cdp-open-option]');
    this.closeOptionButtons = this.querySelectorAll('[data-cdp-close-option]');
    this.variantInputs = this.querySelectorAll('[data-cdp-option]');
    this.variantIdInputs = this.querySelectorAll('[data-cdp-variant-id]');
    this.accordions = this.querySelectorAll('[data-cdp-accordion]');
    this.variantData = this.parseVariantData();
    this.selectedVariant = null;
    this.boundCartUpdate = (event) => this.handleCartUpdate(event);
    this.boundResumeActiveVideo = () => {
      if (!document.hidden) this.syncActiveGalleryMedia();
    };
    this.boundMobileVideoPlay = () => {
      const video = this.galleryContent?.querySelector('video');
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.play().catch(() => {});
    };
    this.addButtons = this.querySelectorAll('[data-cdp-add-button]');

    this.galleryButtons.forEach((button) => {
      button.addEventListener('click', () => this.selectMedia(button));
    });

    this.updateGalleryProgress();
    this.preloadGalleryVideos();
    this.syncActiveGalleryMedia();
    document.addEventListener('visibilitychange', this.boundResumeActiveVideo);
    window.addEventListener('pageshow', this.boundResumeActiveVideo);
    this.galleryMedia?.addEventListener('touchstart', this.boundMobileVideoPlay, { passive: true });

    if (this.galleryMedia && this.galleryButtons.length > 1) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.isSwiping = false;

      this.galleryMedia.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
      this.galleryMedia.addEventListener('touchmove', (event) => this.handleTouchMove(event), { passive: true });
      this.galleryMedia.addEventListener('touchend', (event) => this.handleTouchEnd(event));
    }

    this.openAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.openDrawer(this.advisorDrawer, 'custom-default-product-drawer-open'));
    });

    this.closeAdvisorButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(this.advisorDrawer, 'custom-default-product-drawer-open'));
    });

    this.openOptionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const drawer = this.querySelector(`[data-cdp-option-drawer="${button.dataset.cdpOpenOption}"]`);
        this.closeAllOptionDrawers();
        this.openDrawer(drawer, 'custom-default-product-option-open');
      });
    });

    this.closeOptionButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer(button.closest('[data-cdp-option-drawer]'), 'custom-default-product-option-open'));
    });

    this.addEventListener('click', (event) => this.handleBackdropClick(event));
    this.addEventListener('keydown', (event) => this.handleKeydown(event));

    this.variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        this.updateVariant(input);
        this.closeDrawer(input.closest('[data-cdp-option-drawer]'), 'custom-default-product-option-open');
      });
    });

    this.accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary');
      summary?.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleAccordion(accordion);
      });
    });

    this.addButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!button.disabled) this.setAddToCartLoading(true);
      });
    });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);
    this.updateVariant();
    this.openAdvisorAfterContactSubmit();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.boundCartUpdate);
    document.removeEventListener('visibilitychange', this.boundResumeActiveVideo);
    window.removeEventListener('pageshow', this.boundResumeActiveVideo);
    this.galleryMedia?.removeEventListener('touchstart', this.boundMobileVideoPlay);
    this.preloadedGalleryVideos?.forEach((video) => video.remove());
  }

  parseVariantData() {
    const script = this.querySelector('[data-cdp-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.isSwiping = false;
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return;

    const deltaX = event.touches[0].clientX - this.touchStartX;
    const deltaY = event.touches[0].clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      this.isSwiping = true;
    }
  }

  handleTouchEnd(event) {
    if (!this.isSwiping) {
      this.touchStartX = 0;
      this.touchStartY = 0;
      return;
    }

    const deltaX = event.changedTouches[0].clientX - this.touchStartX;
    const threshold = 40;

    if (deltaX > threshold) {
      this.swipeGallery(-1);
    } else if (deltaX < -threshold) {
      this.swipeGallery(1);
    }

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.isSwiping = false;
  }

  swipeGallery(direction) {
    const buttons = Array.from(this.galleryButtons);
    if (!buttons.length) return;

    this.currentMediaIndex = (this.currentMediaIndex + direction + buttons.length) % buttons.length;
    this.selectMedia(buttons[this.currentMediaIndex]);
  }

  selectMedia(button, animate = true) {
    const src = button.getAttribute('data-cdp-image');
    const srcset = button.getAttribute('data-cdp-srcset');
    const alt = button.getAttribute('data-cdp-alt') || '';
    const mediaTemplate = button.querySelector('[data-cdp-media-template]');
    const buttons = Array.from(this.galleryButtons);
    const nextIndex = buttons.indexOf(button);
    let direction = nextIndex >= this.currentMediaIndex ? 1 : -1;

    if (this.currentMediaIndex === buttons.length - 1 && nextIndex === 0) direction = 1;
    if (this.currentMediaIndex === 0 && nextIndex === buttons.length - 1) direction = -1;

    if (mediaTemplate && this.galleryContent) {
      if (animate) this.transitionGalleryContent(mediaTemplate, direction);
      else this.replaceGalleryContent(mediaTemplate);
    } else if (animate) this.transitionGalleryImage(src, srcset, alt, direction);
    else this.setGalleryImage(src, srcset, alt);
    this.galleryButtons.forEach((thumb) => thumb.setAttribute('aria-current', 'false'));
    button.setAttribute('aria-current', 'true');

    this.currentMediaIndex = nextIndex;
    this.updateGalleryProgress();
  }

  replaceGalleryContent(mediaTemplate) {
    if (!this.galleryContent || !mediaTemplate) return;
    this.galleryContent.querySelectorAll('video').forEach((video) => video.pause());
    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();
  }

  transitionGalleryContent(mediaTemplate, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryContent?.animate === 'function';
    if (!canAnimate || !this.galleryMedia) return this.replaceGalleryContent(mediaTemplate);

    this.galleryMedia.querySelectorAll('[data-cdp-transition-content]').forEach((content) => content.remove());
    this.galleryContent.getAnimations().forEach((animation) => animation.cancel());
    const outgoing = this.galleryContent.cloneNode(false);
    outgoing.removeAttribute('data-cdp-main-media-content');
    outgoing.setAttribute('data-cdp-transition-content', '');
    outgoing.append(...this.galleryContent.childNodes);
    Object.assign(outgoing.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', zIndex: '1', pointerEvents: 'none' });
    this.galleryMedia.append(outgoing);
    this.galleryContent.replaceChildren(mediaTemplate.content.cloneNode(true));
    this.syncActiveGalleryMedia();
    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoing.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing).finished.catch(() => {}).finally(() => {
      outgoing.querySelectorAll('video').forEach((video) => video.pause());
      outgoing.remove();
    });
    this.galleryContent.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  syncActiveGalleryMedia() {
    this.galleryImage = this.galleryContent?.querySelector('[data-cdp-main-image]') || null;
    const video = this.galleryContent?.querySelector('video');
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.autoplay = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const playVideo = () => {
        video.muted = true;
        video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', playVideo, { once: true });
      video.addEventListener('loadeddata', playVideo, { once: true });
      video.addEventListener('canplay', playVideo, { once: true });
      if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
      playVideo();
      requestAnimationFrame(playVideo);
      window.setTimeout(playVideo, 250);
      window.setTimeout(playVideo, 1000);
    }
  }

  preloadGalleryVideos() {
    this.preloadedGalleryVideos = [];
    this.querySelectorAll('template[data-cdp-media-template]').forEach((template) => {
      const sourceVideo = template.content.querySelector('video');
      if (!sourceVideo) return;

      const video = sourceVideo.cloneNode(true);
      video.removeAttribute('autoplay');
      video.removeAttribute('loop');
      video.muted = true;
      video.defaultMuted = true;
      video.preload = 'auto';
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('aria-hidden', 'true');
      video.tabIndex = -1;
      Object.assign(video.style, {
        position: 'absolute',
        width: '1px',
        height: '1px',
        opacity: '0',
        pointerEvents: 'none',
      });
      this.append(video);
      video.load();
      this.preloadedGalleryVideos.push(video);
    });
  }

  transitionGalleryImage(src, srcset, alt, direction = 1) {
    const canAnimate = window.matchMedia('(max-width: 1199px)').matches
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      && typeof this.galleryImage?.animate === 'function';

    if (!canAnimate || !this.galleryMedia) {
      this.setGalleryImage(src, srcset, alt);
      return;
    }

    this.galleryMedia.querySelectorAll('[data-cdp-transition-image]').forEach((image) => image.remove());
    this.galleryImage.getAnimations().forEach((animation) => animation.cancel());

    const outgoingImage = this.galleryImage.cloneNode(true);
    outgoingImage.removeAttribute('data-cdp-main-image');
    outgoingImage.setAttribute('data-cdp-transition-image', '');
    outgoingImage.setAttribute('aria-hidden', 'true');
    Object.assign(outgoingImage.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      zIndex: '1',
      pointerEvents: 'none',
      objectFit: 'cover',
    });
    this.galleryMedia.append(outgoingImage);

    this.setGalleryImage(src, srcset, alt);
    const offset = direction >= 0 ? 100 : -100;
    const timing = { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' };
    outgoingImage.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-offset}%)` }], timing)
      .finished.catch(() => {}).finally(() => outgoingImage.remove());
    this.galleryImage.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], timing);
  }

  updateGalleryProgress() {
    if (!this.galleryProgress) return;

    const count = this.galleryButtons.length;
    this.galleryProgress.hidden = count <= 1;
    this.galleryProgress.style.setProperty('--media-progress-count', Math.max(count, 1));
    this.galleryProgress.style.setProperty('--media-progress-index', Math.max(this.currentMediaIndex, 0));
  }

  setGalleryImage(src, srcset, alt) {
    if (!this.galleryImage || !src) return;

    this.galleryImage.setAttribute('src', src);
    if (srcset) this.galleryImage.setAttribute('srcset', srcset);
    this.galleryImage.setAttribute('alt', alt || '');
  }

  openDrawer(drawer, lockClass) {
    if (!drawer) return;

    drawer.setAttribute('open', '');
    document.documentElement.classList.add(lockClass);

    const firstFocusable = drawer.querySelector('input:not([type="hidden"]), textarea, button');
    window.setTimeout(() => firstFocusable?.focus(), 180);
  }

  closeDrawer(drawer, lockClass) {
    if (!drawer) return;

    drawer.removeAttribute('open');
    document.documentElement.classList.remove(lockClass);
  }

  closeAllOptionDrawers() {
    this.optionDrawers.forEach((drawer) => drawer.removeAttribute('open'));
    document.documentElement.classList.remove('custom-default-product-option-open');
  }

  handleBackdropClick(event) {
    if (event.target === this.advisorDrawer) {
      this.closeDrawer(this.advisorDrawer, 'custom-default-product-drawer-open');
      return;
    }

    if (event.target instanceof Element && event.target.matches('.cdp__option-backdrop')) {
      this.closeDrawer(event.target.closest('[data-cdp-option-drawer]'), 'custom-default-product-option-open');
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape') return;

    if (this.advisorDrawer?.hasAttribute('open')) {
      this.closeDrawer(this.advisorDrawer, 'custom-default-product-drawer-open');
    }

    this.closeAllOptionDrawers();
  }

  updateOptionDisplay(input) {
    const position = input.dataset.optionPosition;
    if (!position) return;

    const selectedValue = this.querySelector(`[data-cdp-selected-value="${position}"]`);
    const selectedIcon = this.querySelector(`[data-cdp-selected-icon="${position}"]`);
    const choice = input.closest('[data-cdp-choice]');
    const choiceIcon = choice?.querySelector('.cdp__choice-icon');
    const optionGroup = input.closest('[data-cdp-option-group]');
    const optionLabel = optionGroup?.querySelector('.cdp__option-label')?.textContent?.toLowerCase() || '';
    const optionValue = input.value.toLowerCase();

    if (selectedValue) selectedValue.textContent = input.value;
    if (selectedIcon && choiceIcon) selectedIcon.innerHTML = choiceIcon.innerHTML;
    optionGroup?.classList.toggle(
      'cdp__option--no-icon',
      optionLabel.includes('number') ||
        optionLabel.includes('letter') ||
        optionValue.includes('number') ||
        optionValue.includes('letter')
    );
  }

  updateVariant(changedInput = null) {
    const selectedVariant = this.getSelectedVariant(changedInput);
    if (!selectedVariant) return;

    this.selectedVariant = selectedVariant;
    this.dataset.selectedVariantId = selectedVariant.id;
    this.syncInputsToVariant(selectedVariant);
    this.updateUrl(selectedVariant);

    this.variantIdInputs.forEach((input) => {
      input.value = selectedVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    this.querySelectorAll('product-form-component').forEach((form) => {
      form.dataset.currentVariantId = selectedVariant.id;
      form.dataset.quantityDefault = selectedVariant.quantityDefault || '1';
    });

    this.addButtons.forEach((button) => {
      button.toggleAttribute('disabled', !selectedVariant.available);
      const text = button.querySelector('.add-to-cart-text__content span span');
      if (text) text.textContent = selectedVariant.available ? button.dataset.availableText : button.dataset.soldOutText;
    });

    this.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (selectedVariant.cartImage) container.dataset.productVariantMedia = selectedVariant.cartImage;
    });

    this.querySelectorAll('[data-cdp-price]').forEach((price) => {
      price.classList.toggle('cdp__price--sale', Boolean(selectedVariant.onSale));
      price.querySelectorAll('[data-cdp-sale-price]').forEach((salePrice) => {
        salePrice.textContent = selectedVariant.price;
      });
      price.querySelectorAll('[data-cdp-compare-price]').forEach((comparePrice) => {
        comparePrice.textContent = selectedVariant.compareAtPrice || '';
        comparePrice.classList.toggle('hidden', !selectedVariant.onSale);
      });
      price.querySelectorAll('[data-cdp-sale-badge]').forEach((badge) => {
        const customBadgeText = badge.dataset.cdpCustomBadgeText || '';
        const badgeText = !selectedVariant.available
          ? badge.dataset.cdpSoldOutBadgeText || 'SOLD OUT'
          : selectedVariant.onSale
            ? badge.dataset.cdpSaleBadgeText || 'SALE'
            : customBadgeText;
        badge.textContent = badgeText;
        Array.from(badge.classList).forEach((className) => {
          if (className.startsWith('badge-')) badge.classList.remove(className);
        });
        if (badgeText) badge.classList.add(`badge-${badgeText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`);
        badge.classList.toggle('hidden', !badgeText);
      });
    });

    this.querySelectorAll('[data-cdp-drawer-price]').forEach((price) => {
      price.textContent = selectedVariant.price;
    });

    this.querySelectorAll('[data-cdp-drawer-variant]').forEach((variantTitle) => {
      variantTitle.textContent = selectedVariant.title === 'Default Title' ? '' : selectedVariant.title;
    });

    this.updateAdvisorFields(selectedVariant);

    if (selectedVariant.featuredImage) {
      const matchingThumb = Array.from(this.galleryButtons).find((thumb) => thumb.getAttribute('data-cdp-image') === selectedVariant.featuredImage);
      if (matchingThumb) this.selectMedia(matchingThumb, false);
      else this.setGalleryImage(selectedVariant.featuredImage, selectedVariant.featuredImageSrcset, selectedVariant.featuredImageAlt);
    }
  }

  getSelectedVariant(changedInput = null) {
    if (!this.variantData.length) return null;

    const selectedOptions = this.getSelectedOptions();
    const exactVariant = this.variantData.find((variant) => {
      return variant.options.every((option, index) => option === selectedOptions[index]);
    });

    if (exactVariant) return exactVariant;

    if (changedInput) {
      const optionIndex = Number(changedInput.dataset.optionPosition || 1) - 1;
      const variantForChangedOption = this.variantData.find((variant) => variant.options[optionIndex] === changedInput.value);
      if (variantForChangedOption) return variantForChangedOption;
    }

    return this.variantData[0] || null;
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-cdp-option-group]')).map((group) => {
      const selected = group.querySelector('[data-cdp-option]:checked');
      return selected?.value;
    });
  }

  syncInputsToVariant(variant) {
    variant.options.forEach((value, index) => {
      const position = String(index + 1);
      const input = this.querySelector(
        `[data-cdp-option][data-option-position="${position}"][value="${this.escapeSelectorValue(value)}"]`
      );
      if (!input) return;

      input.checked = true;
      this.updateOptionDisplay(input);
    });
  }

  escapeSelectorValue(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  updateUrl(variant) {
    if (!variant?.id || !window.history?.replaceState) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateAdvisorFields(variant) {
    const variantTitle = variant.title === 'Default Title' ? '' : variant.title;
    this.querySelectorAll('[data-cdp-advisor-variant]').forEach((input) => {
      input.value = variantTitle;
    });
    this.querySelectorAll('[data-cdp-advisor-url]').forEach((input) => {
      input.value = window.location.href;
    });
  }

  openAdvisorAfterContactSubmit() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('contact_posted') !== 'true') return;

    this.openDrawer(this.advisorDrawer, 'custom-default-product-drawer-open');
  }

  toggleAccordion(accordion) {
    if (accordion.open) {
      this.closeAccordion(accordion);
      return;
    }

    this.accordions.forEach((item) => {
      if (item !== accordion) this.closeAccordion(item);
    });

    this.openAccordion(accordion);
  }

  openAccordion(accordion) {
    const panel = accordion.querySelector('.cdp__accordion-panel');
    if (!panel) return;

    accordion.open = true;
    panel.style.maxHeight = '0px';
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
  }

  closeAccordion(accordion) {
    const panel = accordion.querySelector('.cdp__accordion-panel');
    if (!panel || !accordion.open) return;

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    window.requestAnimationFrame(() => {
      panel.style.maxHeight = '0px';
    });

    const handleTransitionEnd = () => {
      accordion.open = false;
      panel.removeEventListener('transitionend', handleTransitionEnd);
    };

    panel.addEventListener('transitionend', handleTransitionEnd);
  }

  handleCartUpdate(event) {
    if (event.action !== 'add') return;

    const productForm = this.querySelector('product-form-component');
    if (event.target !== productForm && event.target !== this && !this.contains(event.target)) return;

    event.promise
      ?.then(({ detail }) => {
        this.setAddToCartLoading(false);
        if (detail?.didError) return;
        this.openCartDrawer();
      })
      .catch(() => {
        this.setAddToCartLoading(false);
      });
  }

  setAddToCartLoading(isLoading) {
    if (this.loadingTimeout) window.clearTimeout(this.loadingTimeout);

    this.addButtons.forEach((button) => {
      button.toggleAttribute('aria-busy', isLoading);
      button.dataset.loading = isLoading ? 'true' : 'false';
    });

    if (isLoading) {
      this.loadingTimeout = window.setTimeout(() => this.setAddToCartLoading(false), 8000);
    }
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');

    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (window.Shopify?.actions?.openCart) {
      window.Shopify.actions.openCart();
    }
  }
}

if (!customElements.get('custom-default-product')) {
  customElements.define('custom-default-product', CustomDefaultProduct);
}
